import { useState, useRef, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FolderPlus,
  Paperclip,
  Pencil,
  Trash2,
  Loader2,
  FileText,
  Users,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useAuth } from "@/contexts/AuthContext";
import {
  useProjectFoldersQuery,
  useAllProjectFoldersQuery,
  useFolderContentsQuery,
  useFolderCountQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
  useUploadFileMutation,
  useDeleteFileMutation,
} from "@/hooks/queries/useFoldersQuery";
import { useProjectUsersQuery } from "@/hooks/queries/useProjectUsersQuery";
import { RoleType } from "@/types/project";
import type { Folder as FolderType, FileItem } from "@/types/folder";
import { ManageFolderUsersDialog } from "@/components/project/ManageFolderUsersDialog";

interface FoldersTabProps {
  projectId: number;
  targetFolderId?: number;
  readOnly?: boolean;
}

const MAX_FILE_BYTES = 52_428_800; // 50 MB

const ALLOWED_EXTENSIONS = new Set([
  // Only file types that the backend redaction pipeline can actually filter are
  // permitted. Images and the legacy binary Office formats (.doc, .xls, .ppt)
  // cannot be programmatically redacted and are therefore not allowed.
  // PDF
  ".pdf",
  // Word
  ".docx",
  // Excel
  ".xlsx",
  // PowerPoint
  ".pptx",
  // Plain text
  ".txt", ".md", ".markdown", ".csv",
  // Source code
  ".js", ".ts", ".jsx", ".tsx",
  ".py", ".java", ".cs", ".cpp", ".cc", ".cxx", ".c", ".h", ".hpp",
  ".go", ".rb", ".php", ".rs", ".swift", ".kt",
  ".html", ".htm", ".css", ".scss", ".sass",
]);

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface FileNodeProps {
  file: FileItem;
  depth: number;
  canManage: boolean;
  readOnly?: boolean;
  onDelete: (file: FileItem) => void;
}

function FileNode({ file, depth, canManage, readOnly, onDelete }: FileNodeProps) {
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  return (
    <div
      className="group flex items-center gap-2 py-1.5 px-2 text-muted-foreground"
      style={{ paddingLeft: `${depth * 20 + 28}px` }}
    >
      <FileText className="w-4 h-4 shrink-0" />
      <span className="text-sm truncate flex-1">{file.originalFileName}</span>
      <span className="text-xs shrink-0">{formatBytes(file.fileSizeBytes)}</span>
      {!readOnly && canManage && (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-destructive hover:text-destructive flex md:hidden md:group-hover:flex"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(file);
                }}
                aria-label={t("deleteFile")}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("deleteFile")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

interface FolderNodeProps {
  folder: FolderType;
  depth: number;
  canManage: boolean;
  canManageFolderUsers: boolean;
  projectId: number;
  onAddSubfolder: (parentId: number) => void;
  onRename: (folder: FolderType) => void;
  onDelete: (folder: FolderType) => void;
  onUpload: (folderId: number, file: File) => void;
  onDeleteFile: (file: FileItem) => void;
  isUploading: boolean;
  targetFolderId?: number;
  ancestorIds?: Set<number>;
  readOnly?: boolean;
}

function FolderNode({ folder, depth, canManage, canManageFolderUsers, projectId, onAddSubfolder, onRename, onDelete, onUpload, onDeleteFile, isUploading, targetFolderId, ancestorIds, readOnly }: FolderNodeProps) {
  const isTarget = targetFolderId === folder.id;
  const shouldAutoExpand = isTarget || (ancestorIds?.has(folder.id) ?? false);
  const [expanded, setExpanded] = useState(shouldAutoExpand);
  const [manageUsersOpen, setManageUsersOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  // Re-expand when targetFolderId changes (e.g. navigating to a different folder while already on the page)
  useEffect(() => {
    if (shouldAutoExpand) setExpanded(true);
  }, [shouldAutoExpand]);

  // Contents (subfolders + files) are only fetched when the node is first expanded
  const {
    data: contents,
    isLoading: loadingContents,
    isFetched,
  } = useFolderContentsQuery(folder.id, expanded);

  const subfolders = contents?.subfolders ?? [];
  const files = contents?.files ?? [];
  const hasItems = subfolders.length > 0 || files.length > 0;

  // Show chevron until we know for sure there are no children
  const showChevron = !isFetched || hasItems;
  const isOpen = expanded && hasItems;

  function toggle() {
    setExpanded((v) => !v);
  }

  return (
    <div>
      <div
        className={`group flex items-center gap-1 py-1.5 px-2 rounded-md hover:bg-muted/50 cursor-pointer${isTarget ? " ring-2 ring-primary ring-inset" : ""}`}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        {/* Expand/collapse — shows spinner while loading */}
        <button
          className="w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-foreground shrink-0"
          onClick={toggle}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {loadingContents ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : showChevron ? (
            expanded ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )
          ) : null}
        </button>

        {/* Folder icon */}
        <span className="text-muted-foreground shrink-0" onClick={toggle}>
          {isOpen ? (
            <FolderOpen className="w-4 h-4" />
          ) : (
            <Folder className="w-4 h-4" />
          )}
        </span>

        {/* Name */}
        <span className="flex-1 text-sm truncate" onClick={toggle}>
          {folder.name}
        </span>

        {/* Actions — always visible on mobile, visible on hover on desktop, only for users who can manage */}
        {!readOnly && (canManage || canManageFolderUsers) && (
        <div className="flex md:hidden md:group-hover:flex items-center gap-0.5 shrink-0">
          <TooltipProvider delayDuration={300}>
            {canManageFolderUsers && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      setManageUsersOpen(true);
                    }}
                    aria-label={t("manageFolderUsers")}
                  >
                    <Users className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("manageFolderUsers")}</TooltipContent>
              </Tooltip>
            )}

            {canManage && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      disabled={isUploading}
                      aria-label={t("uploadFile")}
                    >
                      {isUploading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Paperclip className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("uploadFile")}</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddSubfolder(folder.id);
                      }}
                      aria-label={t("newSubfolder")}
                    >
                      <FolderPlus className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("newSubfolder")}</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRename(folder);
                      }}
                      aria-label={t("renameFolder")}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("renameFolder")}</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(folder);
                      }}
                      aria-label={t("deleteFolder")}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("deleteFolder")}</TooltipContent>
                </Tooltip>
              </>
            )}
          </TooltipProvider>
        </div>
        )}
      </div>

      {/* Manage Folder Users Dialog */}
      <ManageFolderUsersDialog
        open={manageUsersOpen}
        onOpenChange={setManageUsersOpen}
        folderId={folder.id}
        folderName={folder.name}
        projectId={projectId}
      />

      {/* Hidden file input — triggered by the upload button */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={Array.from(ALLOWED_EXTENSIONS).join(",")}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            onUpload(folder.id, file);
            e.target.value = '';
          }
        }}
      />

      {/* Render subfolders and files when expanded and loaded */}
      {expanded && !loadingContents && (
        <>
          {subfolders.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              depth={depth + 1}
              canManage={canManage}
              canManageFolderUsers={canManageFolderUsers}
              projectId={projectId}
              onAddSubfolder={onAddSubfolder}
              onRename={onRename}
              onDelete={onDelete}
              onUpload={onUpload}
              onDeleteFile={onDeleteFile}
              isUploading={false}
              targetFolderId={targetFolderId}
              ancestorIds={ancestorIds}
              readOnly={readOnly}
            />
          ))}
          {files.map((file) => (
            <FileNode
              key={file.id}
              file={file}
              depth={depth + 1}
              canManage={canManage}
              readOnly={readOnly}
              onDelete={onDeleteFile}
            />
          ))}
        </>
      )}
    </div>
  );
}

export function FoldersTab({ projectId, targetFolderId, readOnly }: FoldersTabProps) {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { user, isLoading: authLoading } = useAuth();

  const { data: rootFolders = [], isLoading } = useProjectFoldersQuery(projectId);
  const { data: allFolders = [] } = useAllProjectFoldersQuery(projectId, !!targetFolderId);
  const { data: totalFolderCount } = useFolderCountQuery(projectId);

  // Build ancestor set: walk parentFolderId chain up from targetFolderId
  const ancestorIds = useMemo<Set<number>>(() => {
    if (!targetFolderId || allFolders.length === 0) return new Set();
    const parentMap = new Map(allFolders.map((f) => [f.id, f.parentFolderId ?? null]));
    const ancestors = new Set<number>();
    let current = parentMap.get(targetFolderId);
    while (current != null) {
      ancestors.add(current);
      current = parentMap.get(current) ?? null;
    }
    return ancestors;
  }, [targetFolderId, allFolders]);
  const createMutation = useCreateFolderMutation();
  const updateMutation = useUpdateFolderMutation();
  const deleteMutation = useDeleteFolderMutation(projectId);
  const uploadMutation = useUploadFileMutation();
  const deleteFileMutation = useDeleteFileMutation();

  // Permission checks
  const currentUserId = Number(user?.id ?? 0);
  const { data: projectUsers = [], isLoading: projectUsersLoading } = useProjectUsersQuery(projectId);
  const permissionsLoading = authLoading || projectUsersLoading;
  const currentMembership = projectUsers.find((pu) => pu.userId === currentUserId);
  const isOwner = currentMembership?.roleType === RoleType.Owner;
  const isEditor = currentMembership?.roleType === RoleType.Editor;
  const canManage = isOwner || isEditor;
  // Manage folder users button: visible to project owners and editors.
  // Project editors can add users; project owners and folder owners can also
  // change roles and remove users (enforced inside the dialog and on the backend).
  const canManageFolderUsers = isOwner || isEditor;

  // Track which folder is currently being uploaded to
  const [uploadingFolderId, setUploadingFolderId] = useState<number | null>(null);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createParentId, setCreateParentId] = useState<number | null>(null);
  const [newFolderName, setNewFolderName] = useState("");

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState<FolderType | null>(null);
  const [renameName, setRenameName] = useState("");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingFolder, setDeletingFolder] = useState<FolderType | null>(null);

  const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false);
  const [deletingFile, setDeletingFile] = useState<FileItem | null>(null);

  function openCreateDialog(parentId: number | null) {
    setCreateParentId(parentId);
    setNewFolderName("");
    setCreateDialogOpen(true);
  }

  function openRenameDialog(folder: FolderType) {
    setRenamingFolder(folder);
    setRenameName(folder.name);
    setRenameDialogOpen(true);
  }

  function openDeleteDialog(folder: FolderType) {
    setDeletingFolder(folder);
    setDeleteDialogOpen(true);
  }

  function handleCreate() {
    if (!newFolderName.trim() || !user) return;
    createMutation.mutate(
      {
        name: newFolderName.trim(),
        projectId,
        parentFolderId: createParentId ?? undefined,
        sourceType: "explicit",
        createdBy: user.email,
      },
      { onSuccess: () => setCreateDialogOpen(false) }
    );
  }

  function handleRename() {
    if (!renameName.trim() || !renamingFolder || !user) return;
    updateMutation.mutate(
      {
        id: renamingFolder.id,
        command: {
          id: renamingFolder.id,
          name: renameName.trim(),
          parentFolderId: renamingFolder.parentFolderId,
          sourceType: renamingFolder.sourceType,
          updatedBy: user.email,
        },
      },
      { onSuccess: () => setRenameDialogOpen(false) }
    );
  }

  function handleDelete() {
    if (!deletingFolder) return;
    deleteMutation.mutate(
      { id: deletingFolder.id, parentFolderId: deletingFolder.parentFolderId },
      {
        onSuccess: () => setDeleteDialogOpen(false),
        onError: () => setDeleteDialogOpen(false),
      }
    );
  }

  function handleUpload(folderId: number, file: File) {
    if (file.size > MAX_FILE_BYTES) {
      toast.error(t("fileTooLarge"));
      return;
    }
    const dotIdx = file.name.lastIndexOf(".");
    const ext = dotIdx === -1 ? "" : file.name.slice(dotIdx).toLowerCase();
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      toast.error(t("fileTypeNotAllowed"));
      return;
    }
    setUploadingFolderId(folderId);
    uploadMutation.mutate(
      { folderId, file, projectId },
      { onSettled: () => setUploadingFolderId(null) }
    );
  }

  function openDeleteFileDialog(file: FileItem) {
    setDeletingFile(file);
    setDeleteFileDialogOpen(true);
  }

  function handleDeleteFile() {
    if (!deletingFile) return;
    deleteFileMutation.mutate(
      { folderId: deletingFile.folderId, fileId: deletingFile.id, projectId },
      {
        onSuccess: () => setDeleteFileDialogOpen(false),
        onError: () => setDeleteFileDialogOpen(false),
      }
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          {totalFolderCount ?? rootFolders.length} {t("folders").toLowerCase()}
        </h3>
        {(canManage || permissionsLoading) && !readOnly && (
          <Button size="sm" onClick={() => openCreateDialog(null)} disabled={permissionsLoading}>
            <FolderPlus className="w-4 h-4 mr-2" />
            {t("newFolder")}
          </Button>
        )}
      </div>

      {/* Tree */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : rootFolders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
          <Folder className="w-10 h-10 opacity-30" />
          <p className="text-sm">{t("noFolders")}</p>
        </div>
      ) : (
        <div className="border rounded-md divide-y">
          {rootFolders.map((folder) => (
            <FolderNode
              key={folder.id}
              folder={folder}
              depth={0}
              canManage={canManage || permissionsLoading}
              canManageFolderUsers={canManageFolderUsers || permissionsLoading}
              projectId={projectId}
              onAddSubfolder={(parentId) => openCreateDialog(parentId)}
              onRename={openRenameDialog}
              onDelete={openDeleteDialog}
              onUpload={handleUpload}
              onDeleteFile={openDeleteFileDialog}
              isUploading={uploadingFolderId === folder.id}
              targetFolderId={targetFolderId}
              ancestorIds={ancestorIds}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}

      {/* Create / Add subfolder dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {createParentId ? t("newSubfolder") : t("newFolder")}
            </DialogTitle>
          </DialogHeader>
          <Input
            placeholder={t("folderName")}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {t("cancel")}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!newFolderName.trim() || createMutation.isPending}
            >
              {t("createFolder")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("renameFolder")}</DialogTitle>
          </DialogHeader>
          <Input
            placeholder={t("folderName")}
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleRename()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              {t("cancel")}
            </Button>
            <Button
              onClick={handleRename}
              disabled={!renameName.trim() || updateMutation.isPending}
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteFolder")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteFolderConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("deleteFolder")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete file confirmation */}
      <AlertDialog open={deleteFileDialogOpen} onOpenChange={setDeleteFileDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteFile")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteFileConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFile}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("deleteFile")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default FoldersTab;
